import { ref, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { postMarketplaceBlob } from 'dashboard/helpers/marketplaceApi';

// Düşük görme kullanım için paylaşılan sesli okuma motoru (Reklam Raporları +
// Kampanya Detayı ortak kullanır). Birincil: OpenAI TTS (doğal/insan sesi, mp3).
// Uzun metni cümlelere bölüp parça parça seslendirir → her çağrı kısa kalır
// (sunucudaki 15sn rack-timeout'a takılmaz), ses ilk parça hazır olunca başlar
// ve kalan parçalar arka planda inip kesintisiz devam eder. OpenAI sesi
// alınamazsa tarayıcının yerleşik (robotik) sesine düşer — hiç sessiz kalmaz.
export function useAdsSpeech() {
  const route = useRoute();
  const isSpeaking = ref(false);
  const isLoadingAudio = ref(false);
  let audioEl = null;
  // "Durdur"a basınca veya yeni okuma başlayınca uçuştaki indirme/çalmaları
  // geçersiz kılmak için artan sayaç.
  let playToken = 0;
  const browserTts =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  const stopSpeaking = () => {
    playToken += 1;
    if (audioEl) {
      audioEl.pause();
      audioEl = null;
    }
    if (browserTts) window.speechSynthesis.cancel();
    isSpeaking.value = false;
    isLoadingAudio.value = false;
  };

  const browserSpeak = text => {
    if (!browserTts) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'tr-TR';
    utter.rate = 0.98;
    utter.onend = () => {
      isSpeaking.value = false;
    };
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    isSpeaking.value = true;
  };

  // Metni cümle sınırlarından ~600 karakterlik parçalara böler.
  const splitChunks = (text, max = 600) => {
    const sentences = text.split(/(?<=[.!?…])\s+/);
    const chunks = [];
    let cur = '';
    sentences.forEach(s => {
      if (cur && `${cur} ${s}`.length > max) {
        chunks.push(cur.trim());
        cur = s;
      } else {
        cur = cur ? `${cur} ${s}` : s;
      }
    });
    if (cur.trim()) chunks.push(cur.trim());
    return chunks;
  };

  const fetchChunkAudio = text =>
    postMarketplaceBlob(route.params.accountId, 'meta_ads/report_speech', {
      text,
    }).then(blob => URL.createObjectURL(blob));

  const playFrom = (i, chunks, urls, token) => {
    if (token !== playToken) return;
    if (i >= chunks.length) {
      isSpeaking.value = false;
      return;
    }
    const tryPlay = () => {
      if (token !== playToken) return;
      const url = urls[i];
      if (url === 'FAILED') {
        playFrom(i + 1, chunks, urls, token);
        return;
      }
      if (!url) {
        setTimeout(tryPlay, 250); // sonraki parça henüz inmedi, bekle
        return;
      }
      audioEl = new Audio(url);
      audioEl.onended = () => {
        URL.revokeObjectURL(url);
        playFrom(i + 1, chunks, urls, token);
      };
      audioEl.play().catch(() => playFrom(i + 1, chunks, urls, token));
    };
    tryPlay();
  };

  // text verilirse okumaya başlar; okuma sürerken çağrılırsa durdurur (toggle).
  const toggleSpeaking = async text => {
    if (isSpeaking.value || isLoadingAudio.value) {
      stopSpeaking();
      return;
    }
    if (!text) return;
    const chunks = splitChunks(text);
    const token = (playToken += 1);
    const urls = new Array(chunks.length).fill(null);
    isLoadingAudio.value = true;
    try {
      urls[0] = await fetchChunkAudio(chunks[0]);
    } catch {
      isLoadingAudio.value = false;
      browserSpeak(text);
      return;
    }
    if (token !== playToken) return; // okuma bu sırada durdurulduysa
    isLoadingAudio.value = false;
    isSpeaking.value = true;
    (async () => {
      for (let i = 1; i < chunks.length; i += 1) {
        if (token !== playToken) return;
        try {
          // eslint-disable-next-line no-await-in-loop
          urls[i] = await fetchChunkAudio(chunks[i]);
        } catch {
          urls[i] = 'FAILED';
        }
      }
    })();
    playFrom(0, chunks, urls, token);
  };

  onBeforeUnmount(stopSpeaking);

  return { isSpeaking, isLoadingAudio, toggleSpeaking, stopSpeaking };
}
